import { createContext, useState, useContext, useEffect } from 'react';
import apiClient from '@api/client';
import userApi from '@api/auth';
import { applyOriginSettings } from '@data/originSettings';

const AuthContext = createContext(null);
export const QUICK_CRED_KEY = 'sg_quick_cred';

// 바로입장 자동 비밀번호 — 이 브라우저에 보관된 것이 지금 계정 것일 때만 돌려준다
export const readQuickCred = (userId) => {
  try {
    const c = JSON.parse(localStorage.getItem(QUICK_CRED_KEY) || 'null');
    return c && String(c.id) === String(userId) ? c : null;
  } catch { return null; }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // 앱 초기화 시 로컬스토리지에서 토큰/유저 정보 복원
  useEffect(() => {
    const storedToken = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      // Axios 기본 헤더에 토큰 설정
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
    setLoading(false);
  }, []);

  // 회원가입 함수
  const signup = async (email, password, nickname) => {
    try {
      const response = await userApi.signup({ email, password, nickname });
      return { success: true, data: response.data };
    } catch (error) {
      const errorMsg = error.response?.data?.detail || '회원가입에 실패했습니다.';
      return { success: false, error: errorMsg };
    }
  };

  // 로그인 함수
  const login = async (email, password) => {
    try {
      const response = await userApi.login({ email, password });
      const { access_token, user_id, nickname, is_superuser } = response.data;

      // 상태 업데이트
      setToken(access_token);
      const userData = { id: user_id, email, nickname };
      setUser(userData);
      // 운영자 계정만 개발 툴(자유 시점 F6·테스트 도구) 노출 — engine/devAccess.isOperator() 가 이 플래그를 봄.
      // 판정은 **서버가 한다**(account.is_superuser). 예전엔 여기서 이메일을 보고 자체 판정했는데
      // (하드코딩 목록 ['a@a.a'] + /^admin@/), 그 목록이 DB is_superuser 와 갈려서 목록에만 있는
      // 계정이 F1 에디터를 열고 진짜 운영자(is_superuser=t)는 못 여는 상태였다. 판정처를 하나로 모은다.
      // localStorage 는 어차피 유저가 고칠 수 있으니 이 플래그는 UI 노출용일 뿐 —
      // 실제 권한 검사는 서버 라우터가 is_superuser() 로 따로 한다(월드 블록 전체 삭제 등).
      try { localStorage.setItem('sg_is_admin', is_superuser ? '1' : '0'); } catch { /* noop */ }

      // 로컬스토리지 저장
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Axios 기본 헤더 설정
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // 신규 계정·새 기기 — 이 브라우저에 저장된 설정이 하나도 없을 때만 기본 룩을 얹는다.
      // 기존 값이 있으면 손대지 않는다(그건 유저가 고른 것이고, DB 값은 곧이어
      // useGameSettings 가 덮어쓴다). force 를 안 주는 게 그 판정이다.
      await applyOriginSettings({ force: false });

      return { success: true };
    } catch (error) {
      const errorMsg = error.response?.data?.detail || '로그인에 실패했습니다.';
      return { success: false, error: errorMsg };
    }
  };
  // 임시 관리자/게스트 우회 로그인 함수
  const guestLogin = async () => {
    try {
      const response = await userApi.guestLogin();
      const { access_token, user_id, nickname } = response.data;

      // 상태 업데이트
      setToken(access_token);
      const userData = { id: user_id, email: "admin@guest.com", nickname };
      setUser(userData);
      try { localStorage.setItem('sg_is_admin', '0'); } catch { /* noop */ }  // 게스트는 운영자 아님

      // 기본 환경설정(origin) — 정본은 data/originSettings.js (서버를 안 거친다)
      // 게스트는 계정 행이 없어(guest-login 이 랜덤 uid 만 준다) 저장된 설정이 있을 수 없다.
      // 그래서 force — 들어갈 때마다 기준 룩으로 맞춘다. 검증 두 컷이 같은 룩이어야
      // 겹쳐 볼 수 있기 때문이다. **여기여야 하는 이유는 순서다**(applyOriginSettings 주석).
      await applyOriginSettings({ force: true });

      // 로컬스토리지 저장
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Axios 기본 헤더 설정
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { success: true };
    } catch (error) {
      console.error(error);
      // 서버가 준 문구를 그대로 올린다. 게스트 발급에는 관문이 붙어 있어서(core/guard.py)
      // 429「N초 후 다시」·503「가득 찼습니다」 같은 **행동을 알려주는** 응답이 온다.
      // 이걸 통째로 「실패했습니다」로 덮으면 유저는 몇 초 기다리면 되는 상황을
      // 「게임이 고장났다」로 읽고 떠난다 — 관문을 넣는 값어치를 여기서 다 잃는다.
      const detail = error?.response?.data?.detail;
      return { success: false, error: typeof detail === 'string' && detail ? detail : '게스트 로그인에 실패했습니다.' };
    }
  };

  // 바로입장 — 서버가 만든 브로큰N 계정으로 로그인. 자동 비밀번호는 이 브라우저에만 보관한다
  // (QUICK_CRED_KEY) — 계정 설정 창이 보여 주고, 현재 비밀번호 칸을 미리 채운다.
  const quickStart = async () => {
    try {
      const response = await userApi.quickStart();
      const { access_token, user_id, nickname, login_id, password, expires_days } = response.data;
      setToken(access_token);
      const userData = { id: user_id, email: login_id, nickname };
      setUser(userData);
      try {
        localStorage.setItem('sg_is_admin', '0');
        localStorage.setItem(QUICK_CRED_KEY, JSON.stringify({ id: user_id, login_id, password, expires_days, at: Date.now() }));
        localStorage.setItem('aist_login_email', login_id);
      } catch { /* noop */ }
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      await applyOriginSettings({ force: false });
      return { success: true, loginId: login_id, password };
    } catch (error) {
      const detail = error?.response?.data?.detail;
      return { success: false, error: typeof detail === 'string' && detail ? detail : '바로입장에 실패했습니다.' };
    }
  };

  // 계정 설정 창에서 아이디·닉네임이 바뀐 뒤 — 새 토큰(아이디가 토큰 sub 다)과 표시값을 맞춘다
  const applyAccountChange = ({ access_token, login_id, nickname, is_quick }) => {
    if (access_token) {
      setToken(access_token);
      localStorage.setItem('access_token', access_token);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    }
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, email: login_id || prev.email, nickname: nickname || prev.nickname };
      try { localStorage.setItem('user', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
    if (!is_quick) { try { localStorage.removeItem(QUICK_CRED_KEY); } catch { /* noop */ } }
  };

  // 닉네임 변경 — 게스트(DB 행 없음, JWT만 발급) 이름짓기용.
  // 네임플레이트(RpgWorld)와 멀티플레이 WS URL(/ws/{id}/{nickname}) 모두 이 user.nickname을 쓰므로
  // 컨텍스트+로컬스토리지 갱신만으로 인게임 전체에 반영된다.
  const updateNickname = (nickname) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, nickname };
      try { localStorage.setItem('user', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  // 로그아웃 함수
  const logout = async () => {
    // Start with the current Authorization header, then end the local session
    // immediately so the game socket cannot reconnect while the request is pending.
    const logoutRequest = token ? userApi.logout() : Promise.resolve();
    setUser(null);
    setToken(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    localStorage.removeItem('sg_is_admin');
    delete apiClient.defaults.headers.common['Authorization'];
    try {
      await logoutRequest;
    } catch (error) {
      console.error('Logout API Error:', error);
    }
    // 페이지를 새로 불러와 모듈 스토어(인벤·펫·퀘스트·워프·채팅 등)를 통째로 비운다 —
    // SPA 이동만 하면 앞 계정의 상태가 다음 계정 화면에 남는다.
    window.location.replace('/login');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, guestLogin, quickStart, applyAccountChange, signup, logout, updateNickname, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
